import { useState, useEffect, useMemo } from 'react';
import { resolveSedbuk, type SedbukResolution } from '@core/resolveSedbuk';
import { formatSedbukBandLabel, type SedbukBand } from '@core/efficiency';
import {
  listBoilerMakes,
  listBoilerModels,
  findBoilerMatch,
  formatGcNumber,
} from '../utils/boilerLookup';

interface CurrentSystemGcFirstProps {
  gcNumber: string;
  onGcNumberChange: (gcNumber: string) => void;
  sedbukBand: string;
  onSedbukBandChange: (band: string) => void;
  boilerMake?: string;
  onBoilerMakeChange?: (make: string) => void;
  boilerModel?: string;
  onBoilerModelChange?: (model: string) => void;
  className?: string;
}

const SEDBUK_BAND_OPTIONS: Array<{ value: SedbukBand; label: string }> = [
  { value: 'A', label: formatSedbukBandLabel('A') },
  { value: 'B', label: formatSedbukBandLabel('B') },
  { value: 'C', label: formatSedbukBandLabel('C') },
  { value: 'D', label: formatSedbukBandLabel('D') },
  { value: 'E', label: formatSedbukBandLabel('E') },
  { value: 'F', label: formatSedbukBandLabel('F') },
  { value: 'G', label: formatSedbukBandLabel('G') },
];

export function CurrentSystemGcFirst({
  gcNumber,
  onGcNumberChange,
  sedbukBand,
  onSedbukBandChange,
  boilerMake,
  onBoilerMakeChange,
  boilerModel,
  onBoilerModelChange,
  className = '',
}: CurrentSystemGcFirstProps) {
  const [gcInput, setGcInput] = useState(gcNumber || '');
  const [sedbukLookupResult, setSedbukLookupResult] = useState<SedbukResolution | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [lookupAttempted, setLookupAttempted] = useState(false);
  const [makeSelection, setMakeSelection] = useState(boilerMake || '');
  const [modelSelection, setModelSelection] = useState(boilerModel || '');
  const [makeModelResult, setMakeModelResult] = useState<SedbukResolution | null>(null);
  const makeOptions = useMemo(() => listBoilerMakes(), []);
  const modelOptions = useMemo(() => listBoilerModels(makeSelection), [makeSelection]);

  // Perform SEDBUK lookup when GC number changes
  useEffect(() => {
    if (!gcInput || gcInput.trim() === '') {
      setSedbukLookupResult(null);
      setLookupAttempted(false);
      return;
    }

    const formattedGc = formatGcNumber(gcInput);
    if (formattedGc !== gcInput) {
      setGcInput(formattedGc);
    }

    // Attempt SEDBUK lookup
    const result = resolveSedbuk({ gcNumber: formattedGc.trim() });
    setLookupAttempted(true);

    if (result.source === 'gc_lookup' && result.matchedBoiler) {
      // Success! We found a match
      setSedbukLookupResult(result);
      onGcNumberChange(formattedGc.trim());
      
      // Auto-fill band
      if (result.band) {
        onSedbukBandChange(result.band);
      }
      
      // Auto-fill make/model if callbacks provided
      if (onBoilerMakeChange && result.matchedBoiler.make) {
        onBoilerMakeChange(result.matchedBoiler.make);
        setMakeSelection(result.matchedBoiler.make);
      }
      if (onBoilerModelChange && result.matchedBoiler.model) {
        onBoilerModelChange(result.matchedBoiler.model);
        setModelSelection(result.matchedBoiler.model);
      }
    } else {
      // No match found - just update the GC number without setting to 'unknown'
      setSedbukLookupResult(null);
      onGcNumberChange(formattedGc.trim());
    }
    // Note: Intentionally omitting callback dependencies to avoid re-running lookup
    // when parent component re-renders. The lookup should only run when gcInput changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gcInput]);

  const handleGcInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGcInput(formatGcNumber(value));
  };

  useEffect(() => {
    setMakeSelection(boilerMake || '');
  }, [boilerMake]);

  useEffect(() => {
    setModelSelection(boilerModel || '');
  }, [boilerModel]);

  useEffect(() => {
    if (!makeSelection || !modelSelection) {
      setMakeModelResult(null);
      return;
    }

    const match = findBoilerMatch(makeSelection, modelSelection);
    const lookupResult = resolveSedbuk({ make: makeSelection, model: modelSelection });
    setMakeModelResult(lookupResult);

    if (match) {
      const formattedGc = formatGcNumber(match.gcNumber);
      onBoilerMakeChange?.(match.make);
      onBoilerModelChange?.(match.model);
      onGcNumberChange(formattedGc);
      setGcInput(formattedGc);
      if (lookupResult.band) {
        onSedbukBandChange(lookupResult.band);
      } else if (match.band) {
        onSedbukBandChange(match.band);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [makeSelection, modelSelection]);

  const hasValidGcMatch = sedbukLookupResult && sedbukLookupResult.matchedBoiler;
  const hasMakeModelMatch = makeModelResult && makeModelResult.matchedBoiler;
  const showBandPicker = !hasValidGcMatch && !hasMakeModelMatch;
  const showMakeModelSelects = !hasValidGcMatch;

  return (
    <div className={`gc-first-input ${className}`}>
      {/* GC Number Input */}
      <div className="form-group">
        <label htmlFor="gc-number">
          GC Number (Primary)
          <span className="label-hint"> — Find this on your boiler's data plate</span>
        </label>
        <input
          id="gc-number"
          type="text"
          value={gcInput}
          onChange={handleGcInputChange}
          placeholder="e.g., 47-116-30"
          className="form-input"
        />
      </div>

      {/* SEDBUK Lookup Result */}
      {hasValidGcMatch && (
        <div className="sedbuk-result success">
          <div className="sedbuk-result-icon">✓</div>
          <div className="sedbuk-result-content">
            <div className="sedbuk-result-title">Match Found</div>
            <div className="sedbuk-result-details">
              <div><strong>Make:</strong> {sedbukLookupResult.matchedBoiler.make}</div>
              <div><strong>Model:</strong> {sedbukLookupResult.matchedBoiler.model}</div>
              <div><strong>Efficiency:</strong> {sedbukLookupResult.label}</div>
              <div><strong>Band:</strong> {sedbukLookupResult.band}</div>
            </div>
          </div>
        </div>
      )}

      {/* No Match - Show Band Picker */}
      {lookupAttempted && !hasValidGcMatch && gcInput.trim() !== '' && (
        <div className="sedbuk-result warning">
          <div className="sedbuk-result-icon">⚠</div>
          <div className="sedbuk-result-content">
            <div className="sedbuk-result-title">GC Number Not Found</div>
            <p className="helper-text">Please select an efficiency band below:</p>
          </div>
        </div>
      )}

      {hasMakeModelMatch && makeModelResult?.matchedBoiler && (
        <div className="sedbuk-result success">
          <div className="sedbuk-result-icon">✓</div>
          <div className="sedbuk-result-content">
            <div className="sedbuk-result-title">Match Found</div>
            <div className="sedbuk-result-details">
              <div><strong>Make:</strong> {makeModelResult.matchedBoiler.make}</div>
              <div><strong>Model:</strong> {makeModelResult.matchedBoiler.model}</div>
              <div><strong>GC Number:</strong> {makeModelResult.matchedBoiler.gcNumber}</div>
              <div><strong>Efficiency:</strong> {makeModelResult.label}</div>
              <div><strong>Band:</strong> {makeModelResult.band}</div>
            </div>
          </div>
        </div>
      )}

      {/* Band Picker - Only show if no GC match OR no GC entered */}
      {showBandPicker && (
        <div className="form-group">
          <label htmlFor="sedbuk-band">
            {gcInput.trim() === '' ? 'Can\'t find GC? Use efficiency band instead' : 'Efficiency Band (Required)'}
          </label>
          <select
            id="sedbuk-band"
            value={sedbukBand || ''}
            onChange={(e) => onSedbukBandChange(e.target.value)}
            className="form-select"
            required={!hasValidGcMatch}
          >
            <option value="">Select band...</option>
            {SEDBUK_BAND_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Advanced: Make/Model (Optional) */}
      {showMakeModelSelects && (
        <div className="advanced-section">
          <button
            type="button"
            className="advanced-toggle"
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            {showAdvanced ? '− ' : '+ '}Advanced: Select Make/Model
          </button>

          {showAdvanced && (
            <div className="advanced-content">
              <div className="form-group">
                <label htmlFor="boiler-make">Boiler Make</label>
                <select
                  id="boiler-make"
                  value={makeSelection}
                  onChange={(e) => {
                    const value = e.target.value;
                    setMakeSelection(value);
                    setModelSelection('');
                    onBoilerMakeChange?.(value);
                    onBoilerModelChange?.('');
                    onGcNumberChange('');
                    setGcInput('');
                  }}
                  className="form-select"
                  disabled={gcInput.trim() !== ''}
                >
                  <option value="">Select make...</option>
                  {makeOptions.map((make) => (
                    <option key={make} value={make}>
                      {make}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="boiler-model">Boiler Model</label>
                <select
                  id="boiler-model"
                  value={modelSelection}
                  onChange={(e) => {
                    const value = e.target.value;
                    setModelSelection(value);
                    onBoilerModelChange?.(value);
                  }}
                  className="form-select"
                  disabled={!makeSelection || gcInput.trim() !== ''}
                >
                  <option value="">Select model...</option>
                  {modelOptions.map((model) => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
